import React, { createContext, useContext, useState, useEffect } from "react";
import { api } from "@/lib/api";

const translations = {
  fr: {
    nav: { login: "Connexion", join: "Rejoindre", catalog: "Catalogue", brands: "Espace Marques", boutiques: "Boutiques", allRubriques: "Tout le catalogue", brandCta: "Marques & Sponsors" },
    brandSection: {
      kicker: "Marques & Sponsors",
      title: "Faites rayonner votre marque",
      subtitle: "Rejoignez Lumina Africa en tant que marque ou sponsor : présentez votre maison, découvrez les talents les mieux notés et collaborez avec l'élite de la mode africaine.",
      points: ["Un profil marque élégant et vérifié", "Accès aux talents les mieux notés par le Lumina Score", "Visibilité auprès de notre communauté créative"],
      cta: "Créer un profil marque",
      secondary: "Découvrir l'Espace Marques",
    },
    sub: {
      tab: "Abonnement", title: "Abonnement talent",
      hint: "Boostez votre visibilité auprès des marques.",
      current: "Votre plan actuel", none: "Aucun abonnement actif",
      subscribe: "S'abonner", perMonth: "/mois",
      cancel: "Annuler l'abonnement",
      cancelConfirm: "Annuler votre abonnement ? Il restera actif jusqu'à la fin de la période payée.",
      canceling: "Annulation programmée à la fin de la période en cours.",
      renewsOn: "Renouvellement le", views: "Vues du profil par les marques",
      active: "Actif", redirect: "Redirection vers le paiement sécurisé…",
      plans: {
        basic: ["Badge « Membre Lumina »", "1 photo bonus dans la galerie", "Présence dans la recherche standard"],
        pro: ["Tout le plan Basic", "Badge « Lumina Pro »", "Profil mis en avant dans la recherche", "Statistiques de vues par les marques", "Accès prioritaire aux castings"],
        elite: ["Tout le plan Pro", "Badge « Lumina Elite »", "En tête de l'espace marques", "Lookbook PDF généré par IA", "Mise en avant mensuelle sur les réseaux"],
      },
      upgradeTitle: "Passez Pro ou Elite",
      upgradeText: "Boostez votre visibilité, débloquez le badge, les statistiques et le lookbook PDF généré par IA.",
      upgradeCta: "Découvrir les offres",
      lookbook: {
        title: "Lookbook PDF (IA)",
        hint: "Générez votre book PDF de luxe en un clic : couverture, intro rédigée par IA et vos photos sélectionnées.",
        select: "Sélectionnez les photos à inclure",
        selectAll: "Tout sélectionner", clear: "Tout désélectionner",
        generate: "Générer le lookbook PDF", generating: "Génération en cours…",
        empty: "Ajoutez d'abord des photos à votre book de casting ou portfolio.",
        selected: "photo(s) sélectionnée(s)",
        done: "Lookbook généré et téléchargé !",
      },
    },
    hero: {
      kicker: "La passerelle entre l'Afrique et l'Occident",
      titleA: "L'élégance africaine",
      titleB: "au sommet de la",
      titleAccent: "mode mondiale",
      subtitle:
        "Mannequins, stylistes, photographes, marques et agences. Une plateforme d'exception qui met en lumière les talents africains sur la scène internationale.",
      ctaPrimary: "Je suis un créateur",
      ctaSecondary: "Je suis un talent · Découvrir",
    },
    how: {
      label: "How it works",
      title: "Créez avec l'intelligence artificielle",
      note: "Aperçu · bientôt disponible",
      uploadTitle: "Uploade ton croquis / photo d'article",
      upload: "Téléverser",
      selectTitle: "Sélectionne ton modèle IA",
      generate: "Générer le rendu",
    },
    talents: {
      title: "Nos Talents d'Exception",
      subtitle: "Des profils vérifiés, prêts à collaborer partout dans le monde.",
      complete: "Profil 100% Complet",
      verified: "Vérifié",
      message: "Message direct",
      cta: "Voir tous les talents",
      view: "Voir le profil",
    },
    marquee: ["Mannequins", "Stylistes", "Photographes", "Marques", "Agences", "Direction artistique"],
    founder: {
      label: "Le Fondateur",
      name: "Jovani Nken",
      role: "Fondateur & Directeur — Lumina Africa",
      quote: "« Je crois en une Afrique qui rayonne au sommet de la mode mondiale. »",
      bio: "Jovani Nken a fondé Lumina Africa avec une vision claire : révéler les talents africains de la mode et les connecter aux plus grandes maisons du monde. Passionné d'élégance et d'excellence, il fait de Lumina la passerelle entre l'Afrique et l'Occident — une scène où chaque talent trouve sa lumière.",
    },
    manifesto: {
      label: "Le Manifeste",
      items: [
        { n: "01", title: "La passerelle", text: "Nous relions les créateurs occidentaux aux talents africains — un pont d'opportunités, de culture et d'excellence." },
        { n: "02", title: "L'excellence vérifiée", text: "Chaque profil est authentifié. Des mannequins aux stylistes, seule l'exception trouve sa place sur Lumina." },
        { n: "03", title: "La scène mondiale", text: "De Dakar à Paris, de Lagos à Milan — nous portons l'élégance africaine au sommet de la mode mondiale." },
      ],
    },
    roles: {
      photographer: "Photographe",
      model: "Mannequin",
      stylist: "Styliste",
      designer: "Créatrice de marque",
    },
    catalogue: {
      title: "Le Catalogue des Talents",
      subtitle: "Découvrez mannequins, stylistes, photographes et marques d'exception à travers l'Afrique et le monde.",
      joinCta: "S'inscrire sur Lumina",
      profile: "Profil",
      category: "Catégorie",
      region: "Région d'origine",
      city: "Ville",
      country: "Pays",
      search: "Rechercher",
      searchPh: "Rechercher un nom, une spécialité, une ville…",
      gems: "Les Pépites",
      gemsSub: "La sélection d'excellence Lumina, mise en avant automatiquement selon le Lumina Score.",
      score: "Score Lumina",
      gem: "Pépite",
      brandsPage: {
        kicker: "Espace Marques",
        title: "L'excellence, en un coup d'œil",
        subtitle: "Les talents les mieux notés de Lumina Africa, sélectionnés automatiquement par le Lumina Score pour les grandes maisons.",
        browse: "Explorer tout le catalogue",
        empty: "Les Pépites arrivent très bientôt.",
      },
      all: "Tous",
      allF: "Toutes",
      results: "profils",
      message: "Message direct",
      verified: "Vérifié",
      clear: "Réinitialiser les filtres",
      none: "Aucun profil ne correspond à ces filtres.",
      view: "Voir le profil",
      cat: { model: "Mannequins", stylist: "Stylistes", photographer: "Photographes", brand: "Marques", agency: "Agences", events: "Événements" },
      catSing: { model: "Mannequin", stylist: "Styliste", photographer: "Photographe", brand: "Marque", agency: "Agence" },
      regions: { all: "Toutes", west: "Afrique de l'Ouest", east: "Afrique de l'Est", north: "Afrique du Nord", central: "Afrique centrale", southern: "Afrique australe", other: "Autre / International" },
    },
    sections: {
      explore: "Explorer la rubrique",
      viewProfiles: "Découvrir les talents",
      searchPh: "Rechercher un nom, une spécialité, une ville…",
      allRegions: "Toutes les régions",
      results: "profils",
      none: "Aucun talent dans cette rubrique pour le moment.",
      joinCta: "Rejoindre Lumina",
      model: {
        kicker: "Mannequins",
        title: "Les visages de la mode africaine",
        lead: "Silhouettes, présence, charisme. Découvrez les mannequins qui incarnent l'audace des plus grandes maisons, du défilé à l'éditorial.",
      },
      stylist: {
        kicker: "Stylistes & Créateurs",
        title: "L'art de la coupe et du vêtement",
        lead: "Celles et ceux qui imaginent, taillent et subliment. Une génération de créateurs qui réinvente l'élégance, entre héritage et modernité.",
      },
      photographer: {
        kicker: "Photographes",
        title: "Le regard qui révèle",
        lead: "La lumière, le cadre, l'instant. Les photographes qui transforment chaque pièce en image mémorable et chaque talent en icône.",
      },
      brand: {
        kicker: "Marques & Maisons",
        title: "Les maisons qui rayonnent",
        lead: "Des marques panafricaines d'exception, du prêt-à-porter à la haute couture. L'excellence du savoir-faire, offerte au monde.",
      },
      boutiques: {
        kicker: "Boutiques",
        title: "Les Boutiques Lumina",
        lead: "Entrez dans l'univers de nos créateurs. Chaque boutique rassemble les pièces prêt-à-porter et sur-mesure d'une maison, à commander en toute sécurité.",
        pieces: "pièces",
        piece: "pièce",
        visit: "Visiter la boutique",
        empty: "Les boutiques arrivent très bientôt.",
        from: "Dès",
        verified: "Vérifié",
        by: "Boutique de",
        backToShops: "Toutes les boutiques",
        viewProfile: "Voir le profil complet",
        notFound: "Cette boutique est introuvable.",
      },
    },
    footer: {
      tagline: "Là où la mode africaine rayonne.",
      rights: "Tous droits réservés.",
      language: "Langue",
      auto: "Automatique (appareil)",
    },
    auth: {
      loginTitle: "Bon retour parmi nous",
      loginSubtitle: "Connectez-vous pour accéder à votre espace Lumina.",
      registerTitle: "Rejoignez Lumina Africa",
      registerSubtitle: "Créez votre compte et rayonnez sur la scène mondiale.",
      name: "Nom complet",
      email: "Adresse e-mail",
      password: "Mot de passe",
      role: "Je suis",
      loginBtn: "Se connecter",
      registerBtn: "Créer mon compte",
      noAccount: "Pas encore de compte ?",
      haveAccount: "Déjà membre ?",
      signup: "S'inscrire",
      signin: "Se connecter",
      back: "Retour à l'accueil",
      creator: "Créateur / Marque",
      talent: "Talent",
      soon: "Bientôt disponible · Fonctionnalité à venir.",
      google: "Continuer avec Google",
      or: "ou",
      agency: "Agence",
      organizer: "Organisateur",
      brand: "Marque",
      creatorShort: "Styliste / Créateur",
      loading: "Un instant…",
      welcome: "Bienvenue",
      quote: "« L'Afrique au sommet de la mode mondiale. »",
    },
    dash: {
      hello: "Bonjour",
      subtitle: "Voici votre espace Lumina Africa.",
      role: "Rôle",
      email: "E-mail",
      provider: "Connexion via",
      logout: "Se déconnecter",
      explore: "Explorer le catalogue",
      editSoon: "Gestion du profil · bientôt disponible",
      profileTab: "Mon profil",
      security: {
        title: "Sécurité & mot de passe",
        hint: "Changez votre mot de passe. Vous serez toujours connecté sur cet appareil.",
        hintNoPassword: "Votre compte utilise Google. Définissez un mot de passe pour pouvoir aussi vous connecter par e-mail.",
        mustChange: "Vous utilisez un mot de passe temporaire : choisissez maintenant votre propre mot de passe.",
        current: "Mot de passe actuel",
        next: "Nouveau mot de passe (8 caractères min.)",
        confirm: "Confirmer le nouveau mot de passe",
        save: "Mettre à jour le mot de passe",
        ok: "Mot de passe mis à jour.",
        mismatch: "Les deux mots de passe ne correspondent pas.",
      },
      portfolioTab: "Portfolio",
      earningsTab: "Revenus",
      earnPaid: "Déjà reversé",
      earnPending: "En attente de reversement",
      earnNone: "Aucun revenu pour le moment.",
      earnHint: "Vous percevez 80 % de chaque booking payé (Lumina prélève 20 %). Pour les ventes boutique, vous recevez 100 % du prix affiché — les frais de service sont ajoutés en plus pour l'acheteur.",
      nameLabel: "Nom / Marque",
      bioLabel: "Biographie",
      bioPlaceholder: "Présentez votre univers, votre expérience, vos collaborations…",
      cityLabel: "Ville",
      countryLabel: "Pays",
      specialtyLabel: "Spécialité",
      websiteLabel: "Site web",
      instagramLabel: "Instagram",
      roleLabel: "Rôle",
      changePhoto: "Changer la photo",
      uploading: "Envoi…",
      save: "Enregistrer les modifications",
      saving: "Enregistrement…",
      saved: "Profil mis à jour",
      portfolioTitle: "Mon portfolio",
      portfolioHint: "Ajoutez vos meilleures réalisations (images, max 8 Mo).",
      addPhoto: "Ajouter une image",
      empty: "Aucune image pour le moment.",
      remove: "Retirer",
      deletePhoto: "Supprimer la photo",
      casting: {
        tab: "Book casting",
        title: "Book de casting",
        hint: "Ajoutez vos photos par emplacement, comme un vrai book de casting. Modifiable et supprimable à tout moment.",
        add: "Ajouter",
        adding: "Envoi…",
        progress: "emplacements remplis",
        cats: {
          portrait: "Portrait / Visage",
          fulllength: "Plan pied (silhouette entière)",
          front: "Face",
          back: "Dos",
          profile: "Profil",
          smile: "Sourire",
          hands: "Mains / Détails",
          polaroid: "Polaroïds / Digitals",
        },
      },
      completion: {
        title: "Complétion du profil",
        complete: "Profil complet",
        remaining: "Il vous reste à compléter :",
        done: "Bravo, votre profil est complet à 100% !",
        items: {
          photo: "Photo de profil",
          bio: "Biographie (40 caractères min.)",
          specialty: "Spécialité",
          city: "Ville",
          country: "Pays",
          social: "Instagram ou site web",
          portfolio: "Au moins une photo portfolio",
          casting: "Book de casting complet (toutes les catégories)",
        },
      },
      talentTypeLabel: "Métier",
      types: { model: "Mannequin", photographer: "Photographe", stylist: "Styliste" },
      measurementsTitle: "Mensurations",
      measurementsHint: "Ces informations aident les agences à vous booker.",
      m: { height: "Taille (cm)", bust: "Poitrine (cm)", waist: "Tour de taille (cm)", hips: "Hanches (cm)", shoe: "Pointure", hair: "Cheveux", eyes: "Yeux" },
      criteriaTitle: "Critères de recherche",
      criteriaHint: "Précisez les profils que vous recherchez.",
      lookingFor: "Je recherche",
      regions: "Régions / villes",
      regionsPh: "Ex. Dakar, Paris, Lagos…",
      needs: "Description du besoin",
      needsPh: "Décrivez vos campagnes, castings ou collaborations recherchés…",
      shareTitle: "Profil public partageable",
      shareHint: "Partagez ce lien avec les agences et marques pour être découvert et réservé.",
      copyLink: "Copier le lien",
      copied: "Lien copié !",
      viewPublic: "Voir mon profil public",
    },
    pub: {
      notFound: "Profil introuvable.",
      back: "Retour à l'accueil",
      about: "À propos",
      portfolio: "Portfolio",
      casting: "Book de casting",
      measurements: "Mensurations",
      seeking: "Recherche",
      links: "Liens",
      book: "Contacter / Réserver",
      verified: "Profil vérifié",
      based: "Basé à",
      contactNote: "Les coordonnées de ce talent sont protégées. Toute demande passe par Lumina Africa, qui valide chaque offre avant de la transmettre au talent.",
      report: {
        button: "Signaler",
        title: "Signaler ce profil",
        desc: "Aidez-nous à garder Lumina Africa sûr. Votre signalement est confidentiel.",
        reasonsLabel: "Motif",
        reasons: ["Faux profil", "Photos volées", "Contenu inapproprié", "Arnaque / sollicitation", "Autre"],
        message: "Détails (facultatif)",
        messagePlaceholder: "Précisez ce qui pose problème…",
        submit: "Envoyer le signalement",
        cancel: "Annuler",
        success: "Merci, votre signalement a bien été transmis à notre équipe.",
      },
    },
    book: {
      button: "Demander un booking",
      title: "Demande de booking",
      forName: "Booking auprès de",
      yourName: "Votre nom",
      yourEmail: "Votre e-mail",
      date: "Date souhaitée",
      budget: "Budget proposé (€)",
      message: "Message",
      messagePlaceholder: "Décrivez votre projet, le type de prestation, le lieu…",
      send: "Envoyer la demande",
      sending: "Envoi…",
      success: "Demande envoyée ! Elle sera validée par Lumina Africa avant d'être transmise au talent.",
      confirmTitle: "Votre demande a bien été envoyée",
      confirmBody: "Le talent vous répondra sous 48h. Vous recevrez une réponse par e-mail.",
      close: "Fermer",
      cancel: "Annuler",
      loginRequired: "Connectez-vous ou créez un compte pour contacter ce talent.",
    },
    bk: {
      tab: "Demandes",
      adminTitle: "Demandes à valider",
      adminHint: "Validez ou refusez chaque offre avant sa transmission au talent.",
      approve: "Valider",
      reject: "Refuser",
      talentTitle: "Contrats à confirmer",
      talentHint: "Offres validées par Lumina Africa. Acceptez ou refusez le contrat.",
      accept: "J'accepte le contrat",
      decline: "Je refuse",
      none: "Aucune demande pour le moment.",
      from: "De",
      date: "Date souhaitée",
      history: "Historique",
      clientTab: "Mes demandes",
      clientTitle: "Mes demandes de booking",
      clientHint: "Suivez vos demandes, échangez en toute sécurité et confirmez la prestation.",
      pay: "Payer maintenant",
      confirmService: "Confirmer la prestation",
      awaitingOther: "En attente de l'autre partie",
      confirmedByYou: "Vous avez confirmé — en attente de l'autre partie",
      completedNote: "Prestation confirmée des deux côtés.",
      messages: "Messagerie sécurisée",
      messagePh: "Écrire un message…",
      msgSend: "Envoyer",
      noMessages: "Aucun message pour le moment.",
      privacyNote: "Vos coordonnées personnelles restent privées. Tout échange passe par Lumina.",
      status: {
        pending_admin: "En attente Lumina",
        admin_approved: "Validé par Lumina",
        admin_rejected: "Refusé par Lumina",
        talent_accepted: "Contrat accepté",
        talent_declined: "Refusé par le talent",
        paid_escrow: "Payé — fonds sécurisés",
        completed: "Prestation confirmée",
        confirmed: "Confirmé",
      },
    },
    admin: {
      title: "Console Lumina Africa",
      subtitle: "Validez les profils et gérez les contrats de booking.",
      tabProfiles: "Profils",
      tabUsers: "Utilisateurs",
      tabShops: "Boutiques",
      myAccount: "Mon compte",
      stats: {
        models: "Mannequins", stylists: "Stylistes", photographers: "Photographes", brands: "Marques",
        organizers: "Organisateurs", events: "Événements publiés", pendingEvents: "à valider", totalEvents: "au total",
        tickets: "Billets vendus", shopItems: "Pièces boutique", shopSold: "vendues", users: "Comptes", banned: "bannis", reports: "Signalements",
      },
      users: {
        title: "Utilisateurs",
        hint: "Liste réelle de tous les comptes de la base. Validez, gérez, bannissez (réversible) ou supprimez (corbeille 30 jours).",
        search: "Rechercher un nom, un e-mail, une ville…",
        none: "Aucun utilisateur ne correspond.",
        roles: { all: "Tous", model: "Mannequin", stylist: "Styliste", photographer: "Photographe", brand: "Marque", organizer: "Organisateur", other: "Autre" },
        superAdmin: "Super Admin",
        banned: "Banni",
        ban: "Bannir",
        unban: "Débannir",
        delete: "Supprimer",
        view: "Voir",
        joined: "Inscrit le",
        banConfirm: "Bannir ce compte ? Il ne pourra plus se connecter et disparaîtra du catalogue (réversible).",
        banReasonPrompt: "Motif du bannissement (envoyé au membre par e-mail ; laissez vide pour un message standard) :",
        bannedOk: "Compte banni.",
        unbannedOk: "Compte réactivé.",
        banDialog: {
          title: "Bannir ce membre",
          desc: "Choisissez un motif fréquent ou saisissez-le. Il sera envoyé au membre par e-mail.",
          reasonsLabel: "Motifs fréquents",
          reasons: ["Spam / sollicitation", "Faux profil", "Comportement inapproprié", "Contenu interdit", "Contournement de la plateforme"],
          custom: "Motif (modifiable)",
          placeholder: "Expliquez brièvement la raison…",
          durationLabel: "Durée",
          days7: "7 jours",
          days30: "30 jours",
          permanent: "Définitif",
          note: "Note interne (privée)",
          notePlaceholder: "Visible par les admins uniquement — non envoyée au membre",
          noteHint: "Cette note n'est pas incluse dans l'e-mail au membre.",
          confirm: "Bannir et notifier",
          cancel: "Annuler",
        },
      },
      shops: {
        title: "Boutiques",
        hint: "Toutes les pièces mises en vente par les stylistes. Supprimez ce qui ne convient pas (corbeille 30 jours).",
        none: "Aucune pièce en boutique.",
        by: "Par",
        rtw: "Prêt-à-porter",
        custom: "Sur-mesure",
        delete: "Supprimer",
        status: { available: "Disponible", sold_out: "Épuisé", archived: "Archivé" },
      },
      logs: {
        title: "Journal des actions",
        hint: "Trace complète de chaque bannissement, validation, suppression et restauration : qui a fait quoi et quand.",
        empty: "Aucune action enregistrée pour l'instant.",
        none: "Aucune action ne correspond au filtre.",
        search: "Rechercher un membre, un e-mail…",
        allActions: "Toutes les actions",
        last7days: "actions ces 7 derniers jours",
        export: "Exporter (CSV)",
        by: "Par",
        reason: "Motif",
        actions: {
          ban: "Bannissement", unban: "Réactivation",
          verify: "Validation", unverify: "Retrait de validation",
          delete_user: "Suppression de compte", delete_photo: "Suppression de photo",
          delete_booking: "Suppression de contrat", delete_event: "Suppression d'événement",
          delete_shop_item: "Suppression de pièce", restore: "Restauration", purge: "Purge définitive",
          auto_unban: "Levée auto de ban", dismiss_report: "Signalement rejeté", resolve_report: "Signalement traité",
        },
      },
      reports: {
        title: "Signalements",
        hint: "Profils signalés par des membres. Examinez, bannissez ou rejetez.",
        empty: "Aucun signalement en attente.",
        reportedBy: "Signalé par",
        view: "Voir le profil",
        ban: "Bannir",
        dismiss: "Rejeter",
        done: { title: "Traités", dismissed: "Rejeté", banned: "Banni", resolved: "Traité", ban_expired: "Ban expiré" },
      },
      tabBookings: "Contrats",
      tabPayouts: "Reversements",
      markPaid: "Marquer reversé",
      payoutPaid: "Reversé",
      payoutPending: "En attente",
      noPayouts: "Aucun reversement.",
      commissionTotal: "Commission totale",
      sTalents: "Talents",
      sVerified: "Vérifiés",
      sPending: "À valider",
      sPaid: "Payés",
      verify: "Valider",
      unverify: "Retirer",
      verified: "Vérifié",
      pendingV: "En attente",
      noProfiles: "Aucun profil.",
      amountLabel: "Montant final (€)",
      approve: "Valider l'offre",
      reject: "Refuser",
      budgetProposed: "Budget proposé",
      commissionNote: "Commission 20 % · Net talent",
      noBookings: "Aucune demande.",
      from: "De",
      edit: "Modifier",
      editTitle: "Modifier le profil",
      rubriqueLabel: "Rubrique",
      deleteAccount: "Supprimer le compte",
      deletePhoto: "Supprimer",
      confirmDelete: "Envoyer ce compte et toutes ses données à la corbeille (récupérable 30 jours) ?",
      duplicate: "Doublon possible",
      demo: "Démo",
      saveEdit: "Enregistrer",
      cancelEdit: "Annuler",
      deletedOk: "Compte supprimé.",
      manage: "Gérer",
      tabSpotlight: "À l'honneur",
      tabSourcing: "Sourcing IA",
      tabFinance: "Finances",
      tabEvents: "Événements",
      tabTrash: "Corbeille",
      tabLogs: "Journal",
      tabReports: "Signalements",
      tabOracle: "Stratège IA",
      oracle: {
        title: "L'Oracle de Lumina",
        subtitle: "Votre stratège mode, directeur artistique & rédacteur en chef — ancré dans vos données réelles.",
        newSession: "Nouvelle session",
        sessions: "Sessions",
        noSessions: "Aucune session. Démarrez une conversation stratégique.",
        placeholder: "Pose ta question stratégique…",
        send: "Envoyer",
        thinking: "L'Oracle réfléchit…",
        emptyChat: "Pose une question, ou choisis une suggestion.",
        deleteConfirm: "Supprimer cette session ?",
        kpis: "Données live",
        kpiLabels: {
          users: "Utilisateurs", talents: "Talents", bookingsPaid: "Bookings payés",
          bookingsPending: "En attente", shopRevenue: "CA boutique", luminaRevenue: "Revenus Lumina",
          payoutsDue: "Reversements dus", tickets: "Billets vendus",
        },
        welcome: "Bonjour. Je suis l'Oracle de Lumina. Interroge-moi sur la croissance, les revenus, la direction artistique ou l'éditorial — je m'appuie sur les données réelles de ta plateforme.",
        prompts: [
          "Quelles sont mes 3 priorités pour augmenter les revenus ce mois-ci ?",
          "Quelles rubriques sous-performent et comment les relancer ?",
          "Plan d'action 30 jours pour attirer plus de créateurs.",
          "Comment transformer Lumina en marque de référence de la mode africaine ?",
        ],
      },
      deleteBookingConfirm: "Envoyer ce contrat à la corbeille (récupérable 30 jours) ?",
      events: {
        title: "Événements",
        hint: "Validez les événements des organisateurs, créez les vôtres, supprimez ce qui ne convient pas.",
        pending: "En attente de validation",
        all: "Tous les événements",
        publish: "Publier",
        reject: "Refuser",
        none: "Aucun événement.",
        create: "Créer un événement (publié immédiatement)",
        sold: "vendus",
        deleteConfirm: "Envoyer cet événement à la corbeille (récupérable 30 jours) ?",
      },
      trash: {
        title: "Corbeille",
        hint: "Tout ce que vous supprimez est conservé 30 jours ici. Restaurez en un clic ou supprimez définitivement.",
        empty: "La corbeille est vide.",
        restore: "Restaurer",
        purge: "Supprimer définitivement",
        purgeConfirm: "Supprimer définitivement ? Cette action est irréversible.",
        restored: "Élément restauré !",
        purged: "Supprimé définitivement.",
        deletedAt: "Supprimé le",
        expires: "Expire dans",
        days: "j",
        kinds: { user: "Compte", booking: "Contrat", event: "Événement", collection_item: "Pièce boutique", photo: "Photo" },
      },
      finance: {
        title: "Finances & MRR",
        mrr: "Revenu mensuel récurrent (MRR)",
        goal: "Objectif",
        subscribers: "Abonnés actifs",
        revenueTotal: "Revenu total (abonnements + commissions)",
        monthRevenue: "Revenu ce mois-ci",
        byMonth: "Détail par mois",
        month: "Mois", subs: "Abonnements", commission: "Commissions", gross: "Volume bookings", revenue: "Revenu",
        export: "Exporter en CSV",
        empty: "Aucune donnée financière pour le moment.",
      },
      sourcing: {
        title: "Campagnes de prospection IA",
        hint: "Génère des messages de prospection ciblés (email + DM) à copier-coller pour recruter marques et talents.",
        audience: "Cible",
        brands: "Marques (ex. LVMH, Nivea)",
        talents: "Talents (ex. mannequins Dakar)",
        target: "Nom de la cible",
        targetPh: "Ex. LVMH, Nivea, mannequins à Lagos…",
        angle: "Angle / précisions (optionnel)",
        anglePh: "Ex. campagne printemps, valeurs de la marque…",
        generate: "Générer la campagne",
        generating: "Rédaction en cours…",
        subject: "Objet de l'email",
        email: "Email de prospection",
        dm: "Message court (DM)",
        copy: "Copier",
        copied: "Copié !",
      },
      spotlight: {
        title: "Talent Elite du mois",
        hint: "Sélectionnez un talent Elite et générez le post réseaux (texte + visuel IA), prêt à publier.",
        pick: "Choisir un talent Elite",
        none: "Aucun talent Elite actif pour le moment. Les abonnés Elite apparaîtront ici.",
        generate: "Générer le post",
        generating: "Génération du post (texte + visuel)…",
        caption: "Texte du post",
        copyCaption: "Copier le texte",
        copied: "Texte copié !",
        visual: "Visuel généré",
        download: "Télécharger le visuel",
        regenerate: "Régénérer",
        noImage: "Le visuel n'a pas pu être généré, réessayez.",
      },
    },
    pay: {
      title: "Finaliser le paiement",
      subtitle: "Réservation via Lumina Africa",
      amount: "Montant à régler",
      commissionIncl: "Commission Lumina incluse",
      pay: "Payer maintenant",
      payCard: "Payer par carte (Stripe)",
      payPaystack: "Mobile Money / carte (Paystack)",
      orPay: "ou",
      recommended: "Recommandé",
      processing: "Redirection vers le paiement sécurisé…",
      checking: "Vérification du paiement…",
      notReady: "Cette réservation n'est pas prête pour le paiement.",
      alreadyPaid: "Cette réservation est déjà payée.",
      successTitle: "Paiement confirmé !",
      successBody: "Votre réservation est confirmée. Lumina Africa vous met en relation avec le talent.",
      cancelTitle: "Paiement annulé",
      cancelBody: "Le paiement n'a pas été finalisé. Vous pouvez réessayer.",
      pendingTitle: "Paiement en cours de vérification",
      pendingBody: "Votre paiement est en cours de confirmation. Cela peut prendre quelques instants.",
      retry: "Vérifier à nouveau",
      backHome: "Retour à l'accueil",
    },
    evt: {
      kicker: "Événements",
      title: "Événements de mode",
      subtitle: "Défilés, showcases et expositions organisés par nos partenaires à travers l'Afrique et le monde. Réservez votre place en quelques clics — paiement en 1, 2, 3 ou 4 fois.",
      none: "Aucun événement à venir pour le moment.",
      from: "Billet",
      view: "Voir & réserver",
      soldOut: "Complet",
      remaining: "places restantes",
      date: "Date", time: "Heure", venue: "Lieu", price: "Billet",
      organizedBy: "Organisé par",
      featured: "À l'affiche",
      about: "À propos de l'événement",
      back: "Retour aux événements",
      notFound: "Événement introuvable.",
      ticket: {
        title: "Réserver vos places",
        qty: "Nombre de places",
        plan: "Mode de paiement",
        perInstallment: "par échéance",
        fee: "Frais de service (10%)",
        total: "Total à régler",
        name: "Votre nom",
        email: "Votre e-mail",
        reserve: "Réserver et payer",
        processing: "Redirection vers le paiement sécurisé…",
        note: "Votre billet vous est envoyé automatiquement par e-mail dès le paiement complet. Paiement sécurisé Stripe.",
      },
      order: {
        title: "Ma réservation",
        paid: "Réglé",
        remaining: "Restant à payer",
        payNext: "Payer la prochaine échéance",
        complete: "Billet confirmé !",
        completeBody: "Votre billet vous a été envoyé par e-mail. Vous pouvez aussi le télécharger ci-dessous.",
        partialTitle: "Acompte enregistré",
        partialBody: "Complétez le paiement pour recevoir automatiquement votre billet par e-mail.",
        download: "Télécharger mon billet (PDF)",
        code: "Code billet",
        checking: "Vérification du paiement…",
        notFound: "Réservation introuvable.",
      },
      dash: {
        tab: "Mes événements",
        title: "Mes événements",
        hint: "Publiez vos défilés, showcases et expositions. Chaque événement est validé par Lumina avant mise en ligne. Vous touchez 100% du prix billet — les frais Lumina (10%) sont payés par l'acheteur.",
        create: "Publier l'événement",
        creating: "Publication…",
        fTitle: "Titre de l'événement",
        fDesc: "Description",
        fDate: "Date", fTime: "Heure", fVenue: "Lieu / salle", fCity: "Ville", fCountry: "Pays",
        fPrice: "Prix billet (€)", fCapacity: "Capacité (opt.)",
        fFeatured: "Talents à l'affiche (handles Lumina, séparés par des virgules)",
        created: "Événement soumis ! Il sera publié après validation Lumina.",
        createdAdmin: "Événement publié !",
        addCover: "Affiche",
        sold: "billets vendus",
        revenue: "Revenus",
        status: { pending: "En attente de validation", published: "Publié", rejected: "Refusé" },
        delete: "Supprimer",
        deleteConfirm: "Supprimer cet événement ? (récupérable 30 jours dans la corbeille admin)",
        none: "Aucun événement pour le moment.",
      },
    },
    shop: {
      tab: "Collections",
      title: "Mes Collections — Boutique",
      hint: "Publiez vos créations comme dans une grande maison : pièces prêt-à-porter vendues en ligne (paiement Stripe sécurisé) et pièces sur-mesure sur demande. Vous touchez 100% du prix affiché — les frais Lumina (10%) sont payés par l'acheteur.",
      addItem: "Ajouter une pièce",
      fTitle: "Nom de la pièce",
      fDesc: "Description (inspiration, coupe, détails…)",
      fPrice: "Prix (€)",
      fSizes: "Tailles disponibles",
      fMaterials: "Matières",
      pap: "Prêt-à-porter",
      surMesure: "Sur-mesure",
      save: "Publier la pièce",
      saving: "Publication…",
      created: "Pièce publiée dans votre boutique !",
      soldOut: "Épuisé",
      markSold: "Marquer épuisé",
      markAvailable: "Remettre en vente",
      delete: "Supprimer",
      deleteConfirm: "Supprimer cette pièce de la boutique ?",
      addImage: "Photo",
      none: "Votre boutique est vide. Publiez votre première pièce.",
      inquiries: "Demandes sur-mesure reçues",
      noInquiries: "Aucune demande pour le moment.",
      publicTitle: "La Boutique",
      publicSub: "Collections",
      inquire: "Commander sur-mesure",
      priceOnRequest: "Prix sur demande",
      itemFee: "Frais de service (10%)",
      totalToPay: "Total",
      buyerName: "Votre nom",
      buyerEmail: "Votre e-mail",
      message: "Votre demande (mensurations, envies, délais…)",
      send: "Envoyer la demande",
      sending: "Envoi…",
      inquirySent: "Demande envoyée ! Le créateur vous répondra via Lumina Africa.",
      checkout: "Payer avec Stripe",
      processing: "Redirection vers le paiement sécurisé…",
      successTitle: "Achat confirmé !",
      successBody: "Merci — votre reçu vous a été envoyé par e-mail. Lumina Africa coordonne l'expédition avec le créateur.",
      sizes: "Tailles",
      materials: "Matières",
      choosePayment: "Moyen de paiement",
      paymentSecure: "Montant calculé et vérifié par Lumina. Paiement 100% sécurisé.",
      methodStripe: "Carte bancaire",
      methodPaystack: "Paystack · carte & Mobile Money",
      methodFlutterwave: "Flutterwave · Mobile Money, carte, virement",
      hintStripe: "Europe & international",
      hintPaystack: "Nigeria, Ghana, Afrique du Sud, Kenya…",
      hintFlutterwave: "Mobile Money Afrique de l'Ouest (FCFA) · Orange, Wave, MTN, Moov",
      recommended: "Recommandé",
      payNow: "Régler",
      paystackCharge: "Facturé via Paystack :",
      flutterwaveCharge: "Facturé via Flutterwave :",
      piece: "pièce",
      pieces: "pièces",
      ordersTitle: "Ventes",
      noOrders: "Aucune vente pour l'instant.",
      orderPreparing: "À expédier",
      orderShipped: "Expédiée",
      markShipped: "Marquer comme expédié",
      carrierLabel: "Transporteur (optionnel)",
      trackingLabel: "N° de suivi (optionnel)",
      confirmShip: "Confirmer l'expédition",
      shippedOn: "Expédiée le",
      buyerLabel: "Acheteur",
      cancelShip: "Annuler",
      orderDelivered: "Livrée",
      markDelivered: "Marquer comme livrée",
      downloadReceipt: "Télécharger le reçu",
      deliveredOn: "Livrée le",
    },
    track: {
      title: "Suivi de commande",
      subtitle: "Suivez votre pièce Lumina Africa, étape par étape.",
      notFound: "Commande introuvable ou lien invalide.",
      step1: "Payée",
      step2: "Expédiée",
      step3: "Livrée",
      carrier: "Transporteur",
      trackingNo: "N° de suivi",
      confirmReceived: "J'ai bien reçu ma commande",
      confirming: "Confirmation…",
      receivedDone: "Merci ! Votre réception est confirmée.",
      receipt: "Télécharger le reçu (PDF)",
      item: "Pièce",
      creator: "Créateur",
      total: "Total réglé",
      ordered: "Commandée le",
      receiptNo: "N° de reçu",
    },
  },
  en: {
    nav: { login: "Log in", join: "Join", catalog: "Catalog", brands: "For Brands", boutiques: "Boutiques", allRubriques: "Full catalog", brandCta: "Brands & Sponsors" },
    brandSection: {
      kicker: "Brands & Sponsors",
      title: "Put your brand in the spotlight",
      subtitle: "Join Lumina Africa as a brand or sponsor: showcase your house, discover the top-rated talents and collaborate with the elite of African fashion.",
      points: ["An elegant, verified brand profile", "Access to talents top-rated by the Lumina Score", "Visibility across our creative community"],
      cta: "Create a brand profile",
      secondary: "Explore the Brands space",
    },
    sub: {
      tab: "Membership", title: "Talent membership",
      hint: "Boost your visibility with brands.",
      current: "Your current plan", none: "No active subscription",
      subscribe: "Subscribe", perMonth: "/mo",
      cancel: "Cancel subscription",
      cancelConfirm: "Cancel your subscription? It stays active until the end of the paid period.",
      canceling: "Cancellation scheduled at the end of the current period.",
      renewsOn: "Renews on", views: "Profile views by brands",
      active: "Active", redirect: "Redirecting to secure checkout…",
      plans: {
        basic: ["'Lumina Member' badge", "1 bonus gallery photo", "Standard search visibility"],
        pro: ["Everything in Basic", "'Lumina Pro' badge", "Featured in search results", "Brand view statistics", "Priority casting access"],
        elite: ["Everything in Pro", "'Lumina Elite' badge", "Top of the brands space", "AI-generated PDF lookbook", "Monthly social media feature"],
      },
      upgradeTitle: "Go Pro or Elite",
      upgradeText: "Boost your visibility, unlock your badge, stats and the AI-generated PDF lookbook.",
      upgradeCta: "See the plans",
      lookbook: {
        title: "PDF Lookbook (AI)",
        hint: "Generate your luxury PDF book in one click: cover, AI-written intro and your selected photos.",
        select: "Select the photos to include",
        selectAll: "Select all", clear: "Clear selection",
        generate: "Generate PDF lookbook", generating: "Generating…",
        empty: "Add photos to your casting book or portfolio first.",
        selected: "photo(s) selected",
        done: "Lookbook generated and downloaded!",
      },
    },
    hero: {
      kicker: "The gateway between Africa and the West",
      titleA: "African elegance",
      titleB: "at the peak of",
      titleAccent: "global fashion",
      subtitle:
        "Models, stylists, photographers, brands and agencies. An exceptional platform spotlighting African talent on the international stage.",
      ctaPrimary: "I'm a creator",
      ctaSecondary: "I'm a talent · Discover",
    },
    how: {
      label: "How it works",
      title: "Create with artificial intelligence",
      note: "Preview · coming soon",
      uploadTitle: "Upload your sketch / product photo",
      upload: "Upload",
      selectTitle: "Select your AI model",
      generate: "Generate render",
    },
    talents: {
      title: "Our Exceptional Talents",
      subtitle: "Verified profiles, ready to collaborate anywhere in the world.",
      complete: "100% Complete Profile",
      verified: "Verified",
      message: "Direct message",
      cta: "View all talents",
      view: "View profile",
    },
    marquee: ["Models", "Stylists", "Photographers", "Brands", "Agencies", "Art Direction"],
    founder: {
      label: "The Founder",
      name: "Jovani Nken",
      role: "Founder & Director — Lumina Africa",
      quote: "\u201cI believe in an Africa that shines at the peak of global fashion.\u201d",
      bio: "Jovani Nken founded Lumina Africa with a clear vision: to reveal Africa's fashion talent and connect it to the world's greatest houses. Driven by elegance and excellence, he makes Lumina the gateway between Africa and the West — a stage where every talent finds its light.",
    },
    manifesto: {
      label: "The Manifesto",
      items: [
        { n: "01", title: "The Gateway", text: "We connect Western creators with African talent — a bridge of opportunity, culture and excellence." },
        { n: "02", title: "Verified Excellence", text: "Every profile is authenticated. From models to stylists, only the exceptional belong on Lumina." },
        { n: "03", title: "The Global Stage", text: "From Dakar to Paris, Lagos to Milan — we bring African elegance to the peak of global fashion." },
      ],
    },
    roles: {
      photographer: "Photographer",
      model: "Model",
      stylist: "Stylist",
      designer: "Brand Owner",
    },
    catalogue: {
      title: "The Talent Catalog",
      subtitle: "Discover exceptional models, stylists, photographers and brands across Africa and beyond.",
      joinCta: "Join Lumina",
      profile: "Profile",
      category: "Category",
      region: "Region of origin",
      city: "City",
      country: "Country",
      search: "Search",
      searchPh: "Search a name, specialty, city…",
      gems: "The Gems",
      gemsSub: "Lumina's excellence selection, automatically highlighted by the Lumina Score.",
      score: "Lumina Score",
      gem: "Gem",
      brandsPage: {
        kicker: "For Brands",
        title: "Excellence, at a glance",
        subtitle: "Lumina Africa's top-rated talents, automatically curated by the Lumina Score for leading fashion houses.",
        browse: "Browse the full catalog",
        empty: "The Gems are coming very soon.",
      },
      all: "All",
      allF: "All",
      results: "profiles",
      message: "Direct message",
      verified: "Verified",
      clear: "Reset filters",
      none: "No profile matches these filters.",
      view: "View profile",
      cat: { model: "Models", stylist: "Stylists", photographer: "Photographers", brand: "Brands", agency: "Agencies", events: "Events" },
      catSing: { model: "Model", stylist: "Stylist", photographer: "Photographer", brand: "Brand", agency: "Agency" },
      regions: { all: "All", west: "West Africa", east: "East Africa", north: "North Africa", central: "Central Africa", southern: "Southern Africa", other: "Other / International" },
    },
    sections: {
      explore: "Explore the section",
      viewProfiles: "Discover the talents",
      searchPh: "Search a name, specialty, city…",
      allRegions: "All regions",
      results: "profiles",
      none: "No talent in this section yet.",
      joinCta: "Join Lumina",
      model: {
        kicker: "Models",
        title: "The faces of African fashion",
        lead: "Silhouette, presence, charisma. Meet the models who embody the boldness of the greatest houses, from runway to editorial.",
      },
      stylist: {
        kicker: "Stylists & Designers",
        title: "The art of cut and cloth",
        lead: "Those who imagine, tailor and elevate. A generation of designers reinventing elegance, between heritage and modernity.",
      },
      photographer: {
        kicker: "Photographers",
        title: "The eye that reveals",
        lead: "Light, frame, moment. The photographers who turn every piece into a memorable image and every talent into an icon.",
      },
      brand: {
        kicker: "Brands & Houses",
        title: "The houses that shine",
        lead: "Exceptional pan-African brands, from ready-to-wear to haute couture. Craftsmanship at its finest, offered to the world.",
      },
      boutiques: {
        kicker: "Boutiques",
        title: "The Lumina Boutiques",
        lead: "Step into our designers' worlds. Each boutique gathers a house's ready-to-wear and made-to-order pieces, to order with total security.",
        pieces: "pieces",
        piece: "piece",
        visit: "Visit the boutique",
        empty: "Boutiques are coming very soon.",
        from: "From",
        verified: "Verified",
        by: "Boutique by",
        backToShops: "All boutiques",
        viewProfile: "View full profile",
        notFound: "This boutique could not be found.",
      },
    },
    footer: {
      tagline: "Where African fashion shines.",
      rights: "All rights reserved.",
      language: "Language",
      auto: "Automatic (device)",
    },
    auth: {
      loginTitle: "Welcome back",
      loginSubtitle: "Sign in to access your Lumina space.",
      registerTitle: "Join Lumina Africa",
      registerSubtitle: "Create your account and shine on the world stage.",
      name: "Full name",
      email: "Email address",
      password: "Password",
      role: "I am a",
      loginBtn: "Sign in",
      registerBtn: "Create my account",
      noAccount: "No account yet?",
      haveAccount: "Already a member?",
      signup: "Sign up",
      signin: "Sign in",
      back: "Back to home",
      creator: "Creator / Brand",
      talent: "Talent",
      soon: "Coming soon · Feature on its way.",
      google: "Continue with Google",
      or: "or",
      agency: "Agency",
      organizer: "Event organizer",
      brand: "Brand",
      creatorShort: "Creator",
      loading: "One moment…",
      welcome: "Welcome",
      quote: "« Africa at the peak of global fashion. »",
    },
    dash: {
      hello: "Hello",
      subtitle: "Here is your Lumina Africa space.",
      role: "Role",
      email: "Email",
      provider: "Signed in via",
      logout: "Log out",
      explore: "Explore the catalog",
      editSoon: "Profile management · coming soon",
      profileTab: "My profile",
      security: {
        title: "Security & password",
        hint: "Change your password. You will stay signed in on this device.",
        hintNoPassword: "Your account uses Google. Set a password to also be able to sign in by email.",
        mustChange: "You are using a temporary password: choose your own password now.",
        current: "Current password",
        next: "New password (min. 8 characters)",
        confirm: "Confirm new password",
        save: "Update password",
        ok: "Password updated.",
        mismatch: "The two passwords do not match.",
      },
      portfolioTab: "Portfolio",
      earningsTab: "Earnings",
      earnPaid: "Paid out",
      earnPending: "Awaiting payout",
      earnNone: "No earnings yet.",
      earnHint: "You receive 80% of every paid booking (Lumina takes 20%). For boutique sales you keep 100% of the listed price — the service fee is added on top for the buyer.",
      nameLabel: "Name / Brand",
      bioLabel: "Biography",
      bioPlaceholder: "Introduce your world, experience, collaborations…",
      cityLabel: "City",
      countryLabel: "Country",
      specialtyLabel: "Specialty",
      websiteLabel: "Website",
      instagramLabel: "Instagram",
      roleLabel: "Role",
      changePhoto: "Change photo",
      uploading: "Uploading…",
      save: "Save changes",
      saving: "Saving…",
      saved: "Profile updated",
      portfolioTitle: "My portfolio",
      portfolioHint: "Add your best work (images, max 8 MB).",
      addPhoto: "Add image",
      empty: "No images yet.",
      remove: "Remove",
      deletePhoto: "Delete photo",
      casting: {
        tab: "Casting book",
        title: "Casting book",
        hint: "Add your photos by slot, like a real casting book. Editable and removable anytime.",
        add: "Add",
        adding: "Uploading…",
        progress: "slots filled",
        cats: {
          portrait: "Portrait / Face",
          fulllength: "Full length",
          front: "Front",
          back: "Back",
          profile: "Profile",
          smile: "Smile",
          hands: "Hands / Details",
          polaroid: "Polaroids / Digitals",
        },
      },
      completion: {
        title: "Profile completion",
        complete: "Profile complete",
        remaining: "Still to complete:",
        done: "Well done, your profile is 100% complete!",
        items: {
          photo: "Profile photo",
          bio: "Biography (40 chars min.)",
          specialty: "Specialty",
          city: "City",
          country: "Country",
          social: "Instagram or website",
          portfolio: "At least one portfolio photo",
          casting: "Complete casting book (all categories)",
        },
      },
      talentTypeLabel: "Profession",
      types: { model: "Model", photographer: "Photographer", stylist: "Stylist" },
      measurementsTitle: "Measurements",
      measurementsHint: "This information helps agencies book you.",
      m: { height: "Height (cm)", bust: "Bust (cm)", waist: "Waist (cm)", hips: "Hips (cm)", shoe: "Shoe size", hair: "Hair", eyes: "Eyes" },
      criteriaTitle: "Search criteria",
      criteriaHint: "Specify the profiles you are looking for.",
      lookingFor: "I'm looking for",
      regions: "Regions / cities",
      regionsPh: "e.g. Dakar, Paris, Lagos…",
      needs: "Description of needs",
      needsPh: "Describe the campaigns, castings or collaborations you seek…",
      shareTitle: "Shareable public profile",
      shareHint: "Share this link with agencies and brands to get discovered and booked.",
      copyLink: "Copy link",
      copied: "Link copied!",
      viewPublic: "View my public profile",
    },
    pub: {
      notFound: "Profile not found.",
      back: "Back to home",
      about: "About",
      portfolio: "Portfolio",
      casting: "Casting book",
      measurements: "Measurements",
      seeking: "Looking for",
      links: "Links",
      book: "Contact / Book",
      verified: "Verified profile",
      based: "Based in",
      contactNote: "This talent's contact details are protected. Every request goes through Lumina Africa, which reviews each offer before forwarding it to the talent.",
      report: {
        button: "Report",
        title: "Report this profile",
        desc: "Help us keep Lumina Africa safe. Your report is confidential.",
        reasonsLabel: "Reason",
        reasons: ["Fake profile", "Stolen photos", "Inappropriate content", "Scam / solicitation", "Other"],
        message: "Details (optional)",
        messagePlaceholder: "Tell us what's wrong…",
        submit: "Send report",
        cancel: "Cancel",
        success: "Thank you, your report has been sent to our team.",
      },
    },
    book: {
      button: "Request booking",
      title: "Booking request",
      forName: "Booking with",
      yourName: "Your name",
      yourEmail: "Your email",
      date: "Preferred date",
      budget: "Proposed budget (€)",
      message: "Message",
      messagePlaceholder: "Describe your project, the type of work, the location…",
      send: "Send request",
      sending: "Sending…",
      success: "Request sent! It will be reviewed by Lumina Africa before being forwarded to the talent.",
      confirmTitle: "Your request has been sent",
      confirmBody: "The talent will reply within 48h. You will receive a response by email.",
      close: "Close",
      cancel: "Cancel",
      loginRequired: "Log in or create an account to contact this talent.",
    },
    bk: {
      tab: "Requests",
      adminTitle: "Requests to review",
      adminHint: "Approve or reject each offer before it reaches the talent.",
      approve: "Approve",
      reject: "Reject",
      talentTitle: "Contracts to confirm",
      talentHint: "Offers approved by Lumina Africa. Accept or decline the contract.",
      accept: "I accept the contract",
      decline: "I decline",
      none: "No requests yet.",
      from: "From",
      date: "Preferred date",
      history: "History",
      clientTab: "My requests",
      clientTitle: "My booking requests",
      clientHint: "Track your requests, chat securely and confirm the service.",
      pay: "Pay now",
      confirmService: "Confirm service",
      awaitingOther: "Awaiting the other party",
      confirmedByYou: "You confirmed — awaiting the other party",
      completedNote: "Service confirmed by both parties.",
      messages: "Secure messaging",
      messagePh: "Write a message…",
      msgSend: "Send",
      noMessages: "No messages yet.",
      privacyNote: "Your personal contact details stay private. All exchanges go through Lumina.",
      status: {
        pending_admin: "Awaiting Lumina",
        admin_approved: "Approved by Lumina",
        admin_rejected: "Rejected by Lumina",
        talent_accepted: "Contract accepted",
        talent_declined: "Declined by talent",
        paid_escrow: "Paid — funds secured",
        completed: "Completed",
        confirmed: "Confirmed",
      },
    },
    admin: {
      title: "Lumina Africa Console",
      subtitle: "Validate profiles and manage booking contracts.",
      tabProfiles: "Profiles",
      tabUsers: "Users",
      tabShops: "Shops",
      myAccount: "My account",
      stats: {
        models: "Models", stylists: "Stylists", photographers: "Photographers", brands: "Brands",
        organizers: "Organizers", events: "Published events", pendingEvents: "to review", totalEvents: "total",
        tickets: "Tickets sold", shopItems: "Shop items", shopSold: "sold", users: "Accounts", banned: "banned", reports: "Reports",
      },
      users: {
        title: "Users",
        hint: "Live list of every account in the database. Verify, manage, ban (reversible) or delete (30-day trash).",
        search: "Search a name, email, city…",
        none: "No user matches.",
        roles: { all: "All", model: "Model", stylist: "Stylist", photographer: "Photographer", brand: "Brand", organizer: "Organizer", other: "Other" },
        superAdmin: "Super Admin",
        banned: "Banned",
        ban: "Ban",
        unban: "Unban",
        delete: "Delete",
        view: "View",
        joined: "Joined",
        banConfirm: "Ban this account? They will no longer be able to log in and will disappear from the catalogue (reversible).",
        banReasonPrompt: "Reason for the ban (emailed to the member; leave blank for a standard message):",
        bannedOk: "Account banned.",
        unbannedOk: "Account reactivated.",
        banDialog: {
          title: "Ban this member",
          desc: "Pick a common reason or type one. It will be emailed to the member.",
          reasonsLabel: "Common reasons",
          reasons: ["Spam / solicitation", "Fake profile", "Inappropriate behaviour", "Prohibited content", "Bypassing the platform"],
          custom: "Reason (editable)",
          placeholder: "Briefly explain the reason…",
          durationLabel: "Duration",
          days7: "7 days",
          days30: "30 days",
          permanent: "Permanent",
          note: "Internal note (private)",
          notePlaceholder: "Visible to admins only — not sent to the member",
          noteHint: "This note is not included in the email to the member.",
          confirm: "Ban and notify",
          cancel: "Cancel",
        },
      },
      shops: {
        title: "Shops",
        hint: "Every item put on sale by stylists. Remove anything unsuitable (30-day trash).",
        none: "No shop items.",
        by: "By",
        rtw: "Ready-to-wear",
        custom: "Made-to-measure",
        delete: "Delete",
        status: { available: "Available", sold_out: "Sold out", archived: "Archived" },
      },
      logs: {
        title: "Activity log",
        hint: "A full trail of every ban, validation, deletion and restore: who did what and when.",
        empty: "No actions recorded yet.",
        none: "No action matches the filter.",
        search: "Search a member, email…",
        allActions: "All actions",
        last7days: "actions in the last 7 days",
        export: "Export (CSV)",
        by: "By",
        reason: "Reason",
        actions: {
          ban: "Ban", unban: "Reactivation",
          verify: "Approval", unverify: "Approval removed",
          delete_user: "Account deleted", delete_photo: "Photo deleted",
          delete_booking: "Contract deleted", delete_event: "Event deleted",
          delete_shop_item: "Item deleted", restore: "Restore", purge: "Permanent purge",
          auto_unban: "Auto-unban", dismiss_report: "Report dismissed", resolve_report: "Report resolved",
        },
      },
      reports: {
        title: "Reports",
        hint: "Profiles reported by members. Review, ban or dismiss.",
        empty: "No pending reports.",
        reportedBy: "Reported by",
        view: "View profile",
        ban: "Ban",
        dismiss: "Dismiss",
        done: { title: "Handled", dismissed: "Dismissed", banned: "Banned", resolved: "Handled", ban_expired: "Ban expired" },
      },
      tabBookings: "Contracts",
      tabPayouts: "Payouts",
      markPaid: "Mark paid out",
      payoutPaid: "Paid out",
      payoutPending: "Pending",
      noPayouts: "No payouts.",
      commissionTotal: "Total commission",
      sTalents: "Talents",
      sVerified: "Verified",
      sPending: "To review",
      sPaid: "Paid",
      verify: "Approve",
      unverify: "Revoke",
      verified: "Verified",
      pendingV: "Pending",
      noProfiles: "No profiles.",
      amountLabel: "Final amount (€)",
      approve: "Approve offer",
      reject: "Reject",
      budgetProposed: "Proposed budget",
      commissionNote: "20% commission · Talent net",
      noBookings: "No requests.",
      from: "From",
      edit: "Edit",
      editTitle: "Edit profile",
      rubriqueLabel: "Category",
      deleteAccount: "Delete account",
      deletePhoto: "Delete",
      confirmDelete: "Move this account and all its data to the trash (recoverable for 30 days)?",
      duplicate: "Possible duplicate",
      demo: "Demo",
      saveEdit: "Save",
      cancelEdit: "Cancel",
      deletedOk: "Account deleted.",
      manage: "Manage",
      tabSpotlight: "Spotlight",
      tabSourcing: "AI Sourcing",
      tabFinance: "Finance",
      tabEvents: "Events",
      tabTrash: "Trash",
      tabLogs: "Activity log",
      tabReports: "Reports",
      tabOracle: "AI Strategist",
      oracle: {
        title: "The Lumina Oracle",
        subtitle: "Your fashion strategist, artistic director & editor-in-chief — grounded in your real data.",
        newSession: "New session",
        sessions: "Sessions",
        noSessions: "No session yet. Start a strategic conversation.",
        placeholder: "Ask your strategic question…",
        send: "Send",
        thinking: "The Oracle is thinking…",
        emptyChat: "Ask a question, or pick a suggestion.",
        deleteConfirm: "Delete this session?",
        kpis: "Live data",
        kpiLabels: {
          users: "Users", talents: "Talents", bookingsPaid: "Paid bookings",
          bookingsPending: "Pending", shopRevenue: "Shop sales", luminaRevenue: "Lumina revenue",
          payoutsDue: "Payouts due", tickets: "Tickets sold",
        },
        welcome: "Hello. I am the Lumina Oracle. Ask me about growth, revenue, artistic direction or editorial — I draw on your platform's real data.",
        prompts: [
          "What are my top 3 priorities to grow revenue this month?",
          "Which sections underperform and how do I revive them?",
          "A 30-day action plan to attract more designers.",
          "How do I turn Lumina into a leading African fashion brand?",
        ],
      },
      deleteBookingConfirm: "Move this contract to the trash (recoverable for 30 days)?",
      events: {
        title: "Events",
        hint: "Approve organizers' events, create your own, delete anything unsuitable.",
        pending: "Awaiting approval",
        all: "All events",
        publish: "Publish",
        reject: "Reject",
        none: "No events.",
        create: "Create an event (published immediately)",
        sold: "sold",
        deleteConfirm: "Move this event to the trash (recoverable for 30 days)?",
      },
      trash: {
        title: "Trash",
        hint: "Everything you delete is kept here for 30 days. Restore in one click or delete permanently.",
        empty: "The trash is empty.",
        restore: "Restore",
        purge: "Delete permanently",
        purgeConfirm: "Delete permanently? This cannot be undone.",
        restored: "Item restored!",
        purged: "Permanently deleted.",
        deletedAt: "Deleted on",
        expires: "Expires in",
        days: "d",
        kinds: { user: "Account", booking: "Contract", event: "Event", collection_item: "Shop item", photo: "Photo" },
      },
      finance: {
        title: "Finance & MRR",
        mrr: "Monthly recurring revenue (MRR)",
        goal: "Goal",
        subscribers: "Active subscribers",
        revenueTotal: "Total revenue (subscriptions + commissions)",
        monthRevenue: "Revenue this month",
        byMonth: "Monthly breakdown",
        month: "Month", subs: "Subscriptions", commission: "Commissions", gross: "Bookings volume", revenue: "Revenue",
        export: "Export CSV",
        empty: "No financial data yet.",
      },
      sourcing: {
        title: "AI outreach campaigns",
        hint: "Generate targeted outreach messages (email + DM) to copy-paste and recruit brands and talents.",
        audience: "Audience",
        brands: "Brands (e.g. LVMH, Nivea)",
        talents: "Talents (e.g. Dakar models)",
        target: "Target name",
        targetPh: "e.g. LVMH, Nivea, models in Lagos…",
        angle: "Angle / notes (optional)",
        anglePh: "e.g. spring campaign, brand values…",
        generate: "Generate campaign",
        generating: "Writing…",
        subject: "Email subject",
        email: "Outreach email",
        dm: "Short message (DM)",
        copy: "Copy",
        copied: "Copied!",
      },
      spotlight: {
        title: "Elite Talent of the Month",
        hint: "Pick an Elite talent and generate the social post (AI text + visual), ready to publish.",
        pick: "Choose an Elite talent",
        none: "No active Elite talent yet. Elite subscribers will appear here.",
        generate: "Generate post",
        generating: "Generating post (text + visual)…",
        caption: "Post caption",
        copyCaption: "Copy caption",
        copied: "Caption copied!",
        visual: "Generated visual",
        download: "Download visual",
        regenerate: "Regenerate",
        noImage: "The visual could not be generated, please retry.",
      },
    },
    pay: {
      title: "Complete payment",
      subtitle: "Booking via Lumina Africa",
      amount: "Amount due",
      commissionIncl: "Lumina commission included",
      pay: "Pay now",
      payCard: "Pay by card (Stripe)",
      payPaystack: "Mobile Money / card (Paystack)",
      orPay: "or",
      recommended: "Recommended",
      processing: "Redirecting to secure payment…",
      checking: "Verifying payment…",
      notReady: "This booking is not ready for payment.",
      alreadyPaid: "This booking is already paid.",
      successTitle: "Payment confirmed!",
      successBody: "Your booking is confirmed. Lumina Africa will connect you with the talent.",
      subTitle: "Subscription active!",
      subBody: "Welcome. Your badge and perks are now live on your profile.",
      cancelTitle: "Payment cancelled",
      cancelBody: "The payment was not completed. You can try again.",
      pendingTitle: "Payment is being verified",
      pendingBody: "Your payment is being confirmed. This may take a few moments.",
      retry: "Check again",
      backHome: "Back to home",
    },
    evt: {
      kicker: "Events",
      title: "Fashion events",
      subtitle: "Runway shows, showcases and exhibitions organized by our partners across Africa and the world. Book your seat in a few clicks — pay in 1, 2, 3 or 4 installments.",
      none: "No upcoming events at the moment.",
      from: "Ticket",
      view: "View & book",
      soldOut: "Sold out",
      remaining: "seats left",
      date: "Date", time: "Time", venue: "Venue", price: "Ticket",
      organizedBy: "Organized by",
      featured: "Featured talents",
      about: "About the event",
      back: "Back to events",
      notFound: "Event not found.",
      ticket: {
        title: "Book your seats",
        qty: "Number of seats",
        plan: "Payment plan",
        perInstallment: "per installment",
        fee: "Service fee (10%)",
        total: "Total due",
        name: "Your name",
        email: "Your email",
        reserve: "Book and pay",
        processing: "Redirecting to secure payment…",
        note: "Your ticket is automatically emailed to you once payment is complete. Secure Stripe payment.",
      },
      order: {
        title: "My booking",
        paid: "Paid",
        remaining: "Remaining to pay",
        payNext: "Pay the next installment",
        complete: "Ticket confirmed!",
        completeBody: "Your ticket has been emailed to you. You can also download it below.",
        partialTitle: "Deposit received",
        partialBody: "Complete the payment to automatically receive your ticket by email.",
        download: "Download my ticket (PDF)",
        code: "Ticket code",
        checking: "Verifying payment…",
        notFound: "Booking not found.",
      },
      dash: {
        tab: "My events",
        title: "My events",
        hint: "Publish your runway shows, showcases and exhibitions. Every event is reviewed by Lumina before going live. You receive 100% of the ticket price — the 10% Lumina fee is paid by the buyer.",
        create: "Publish event",
        creating: "Publishing…",
        fTitle: "Event title",
        fDesc: "Description",
        fDate: "Date", fTime: "Time", fVenue: "Venue", fCity: "City", fCountry: "Country",
        fPrice: "Ticket price (€)", fCapacity: "Capacity (opt.)",
        fFeatured: "Featured talents (Lumina handles, comma separated)",
        created: "Event submitted! It will be published after Lumina approval.",
        createdAdmin: "Event published!",
        addCover: "Cover",
        sold: "tickets sold",
        revenue: "Revenue",
        status: { pending: "Awaiting approval", published: "Published", rejected: "Rejected" },
        delete: "Delete",
        deleteConfirm: "Delete this event? (recoverable for 30 days in the admin trash)",
        none: "No events yet.",
      },
    },
    shop: {
      tab: "Collections",
      title: "My Collections — Boutique",
      hint: "Publish your creations like a great fashion house: ready-to-wear pieces sold online (secure Stripe payment) and made-to-order pieces on request. You receive 100% of the listed price — the 10% Lumina fee is paid by the buyer.",
      addItem: "Add a piece",
      fTitle: "Piece name",
      fDesc: "Description (inspiration, cut, details…)",
      fPrice: "Price (€)",
      fSizes: "Available sizes",
      fMaterials: "Materials",
      pap: "Ready-to-wear",
      surMesure: "Made-to-order",
      save: "Publish piece",
      saving: "Publishing…",
      created: "Piece published in your boutique!",
      soldOut: "Sold out",
      markSold: "Mark sold out",
      markAvailable: "Put back on sale",
      delete: "Delete",
      deleteConfirm: "Remove this piece from the boutique?",
      addImage: "Photo",
      none: "Your boutique is empty. Publish your first piece.",
      inquiries: "Made-to-order requests received",
      noInquiries: "No requests yet.",
      publicTitle: "The Boutique",
      publicSub: "Collections",
      inquire: "Order made-to-measure",
      priceOnRequest: "Price on request",
      itemFee: "Service fee (10%)",
      totalToPay: "Total",
      buyerName: "Your name",
      buyerEmail: "Your email",
      message: "Your request (measurements, wishes, timeline…)",
      send: "Send request",
      sending: "Sending…",
      inquirySent: "Request sent! The designer will reply through Lumina Africa.",
      checkout: "Pay with Stripe",
      processing: "Redirecting to secure payment…",
      successTitle: "Purchase confirmed!",
      successBody: "Thank you — your receipt has been emailed. Lumina Africa coordinates shipping with the designer.",
      sizes: "Sizes",
      materials: "Materials",
      choosePayment: "Payment method",
      paymentSecure: "Amount calculated and verified by Lumina. 100% secure payment.",
      methodStripe: "Credit / debit card",
      methodPaystack: "Paystack · card & Mobile Money",
      methodFlutterwave: "Flutterwave · Mobile Money, card, transfer",
      hintStripe: "Europe & international",
      hintPaystack: "Nigeria, Ghana, South Africa, Kenya…",
      hintFlutterwave: "West Africa Mobile Money (FCFA) · Orange, Wave, MTN, Moov",
      recommended: "Recommended",
      payNow: "Pay",
      paystackCharge: "Charged via Paystack:",
      flutterwaveCharge: "Charged via Flutterwave:",
      piece: "piece",
      pieces: "pieces",
      ordersTitle: "Sales",
      noOrders: "No sales yet.",
      orderPreparing: "To ship",
      orderShipped: "Shipped",
      markShipped: "Mark as shipped",
      carrierLabel: "Carrier (optional)",
      trackingLabel: "Tracking number (optional)",
      confirmShip: "Confirm shipment",
      shippedOn: "Shipped on",
      buyerLabel: "Buyer",
      cancelShip: "Cancel",
      orderDelivered: "Delivered",
      markDelivered: "Mark as delivered",
      downloadReceipt: "Download receipt",
      deliveredOn: "Delivered on",
    },
    track: {
      title: "Order tracking",
      subtitle: "Follow your Lumina Africa piece, step by step.",
      notFound: "Order not found or invalid link.",
      step1: "Paid",
      step2: "Shipped",
      step3: "Delivered",
      carrier: "Carrier",
      trackingNo: "Tracking number",
      confirmReceived: "I have received my order",
      confirming: "Confirming…",
      receivedDone: "Thank you! Your delivery is confirmed.",
      receipt: "Download receipt (PDF)",
      item: "Piece",
      creator: "Designer",
      total: "Total paid",
      ordered: "Ordered on",
      receiptNo: "Receipt no.",
    },
  },
};

const RTL = ["ar", "he", "fa", "ur"];

// Langues proposées dans le sélecteur discret (la détection auto reste par défaut)
export const LANGS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "ar", label: "العربية" },
  { code: "zh", label: "中文" },
];

const deepMerge = (base, over) => {
  if (base && typeof base === "object" && !Array.isArray(base) && over && typeof over === "object") {
    const out = {};
    for (const k of Object.keys(base)) out[k] = k in over ? deepMerge(base[k], over[k]) : base[k];
    return out;
  }
  return over === undefined || over === null ? base : over;
};

const LanguageContext = createContext(null);

const detectLang = () => {
  const raw = (typeof navigator !== "undefined" && (navigator.language || navigator.userLanguage)) || "fr";
  return raw.toLowerCase().split("-")[0];
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("lumina_lang_override") || detectLang(); } catch (e) { return detectLang(); }
  });
  const [t, setT] = useState(lang === "en" ? translations.en : translations.fr);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
      document.documentElement.dir = RTL.includes(lang) ? "rtl" : "ltr";
    }
    if (lang === "fr") { setT(translations.fr); return; }
    if (lang === "en") { setT(translations.en); return; }

    let active = true;
    const cacheKey = `lumina_i18n_${lang}_v1`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try { setT(deepMerge(translations.en, JSON.parse(cached))); } catch { setT(translations.fr); }
    } else {
      setT(translations.fr);
    }
    api.post("/i18n/translate", { lang, source: translations.en })
      .then(({ data }) => {
        if (active && data?.strings) {
          setT(deepMerge(translations.en, data.strings));
          try { localStorage.setItem(cacheKey, JSON.stringify(data.strings)); } catch (e) { /* quota */ }
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [lang]);

  // Language follows the device automatically; users can optionally override it.
  const changeLang = (code) => {
    const c = (code || "").toLowerCase().split("-")[0];
    if (!c) return;
    try { localStorage.setItem("lumina_lang_override", c); } catch (e) { /* ignore */ }
    setLang(c);
  };
  const autoLang = () => {
    try { localStorage.removeItem("lumina_lang_override"); } catch (e) { /* ignore */ }
    setLang(detectLang());
  };

  return (
    <LanguageContext.Provider value={{ lang, changeLang, autoLang, setLang, toggle: () => {}, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);

// Langue-agnostique : retrouve l'index d'un motif de signalement quelle que soit la langue (FR ou EN)
export const reportReasonIndex = (reason) => {
  const fr = translations.fr.pub.report.reasons || [];
  const en = translations.en.pub.report.reasons || [];
  const i = fr.indexOf(reason);
  return i >= 0 ? i : en.indexOf(reason);
};

// Clés canoniques stables (indépendantes de la langue) — l'ordre suit les tableaux de libellés i18n
export const REPORT_REASON_KEYS = ["fake_profile", "stolen_photos", "inappropriate", "scam", "other"];
export const BAN_REASON_KEYS = ["spam", "fake_profile", "inappropriate", "prohibited", "bypass"];
export const REPORT_TO_BAN_KEY = { fake_profile: "fake_profile", stolen_photos: "prohibited", inappropriate: "inappropriate", scam: "spam", other: null };
